import { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Paper, 
  Grid, 
  Divider, 
  Alert,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import Hero from '../components/common/Hero';
import { Helmet } from 'react-helmet-async';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HomeIcon from '@mui/icons-material/Home';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import { arcRequestService } from '../services/api';

const ARCRequestForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    description: '',
  });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear validation error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      // Convert FileList to array
      const filesArray = Array.from(e.target.files);
      setFiles(filesArray);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s\-()]{10,15}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Project description is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Submit the ARC request to the backend
      const result = await arcRequestService.submitRequest(formData, files);
      
      if (result.success) {
        // Show success message
        setSubmitSuccess(true);
        
        // Clear form after submission
        setFormData({
          name: '',
          address: '',
          phone: '',
          description: '',
        });
        setFiles([]);
      } else {
        setSubmitError(result.error || 'Failed to submit request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting ARC request:', error);
      setSubmitError('There was a problem submitting your request. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      address: '',
      phone: '',
      description: '',
    });
    setFiles([]);
    setErrors({});
    setSubmitSuccess(false);
    setSubmitError(null);
  };

  return (
    <>
      <Helmet>
        <title>Architectural Review Request Form | Brynleigh Estates HOA</title>
        <meta name="description" content="Submit your Architectural Review Committee (ARC) request for home modifications and improvements in Brynleigh Estates. Get approval for exterior changes." />
        <link rel="canonical" href="https://brynleighestates.com/arc-request" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/arc-request" />
        <meta property="og:title" content="Architectural Review Request Form | Brynleigh Estates HOA" />
        <meta property="og:description" content="Submit your Architectural Review Committee (ARC) request for home modifications and improvements in Brynleigh Estates." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Architectural Review Request Form | Brynleigh Estates HOA" />
        <meta name="twitter:description" content="Submit your ARC request for home modifications in Brynleigh Estates." />
        <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
      </Helmet>
      
      <Hero 
        imageUrl="/images/hills-bg.jpeg"
        title="Architectural Review Request"
        subtitle="Submit your home modification plans for approval"
      />
      
      <Container maxWidth="lg">
        <Box sx={{ my: 6 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              {submitSuccess ? (
                <Paper elevation={0} sx={{ p: 4, mb: 4, border: '1px solid', borderColor: 'success.light', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircleIcon color="success" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5" component="h2">
                      Request Submitted Successfully
                    </Typography>
                  </Box>
                  <Typography paragraph>
                    Thank you for submitting your Architectural Review Committee request. Your submission has been received and will be reviewed by the committee.
                  </Typography>
                  <Typography paragraph>
                    Please allow up to 30 days for the review process. You will be notified by email or phone once a decision has been made.
                  </Typography>
                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button variant="contained" onClick={handleReset}>
                      Submit Another Request
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/')}>
                      Return to Home
                    </Button>
                  </Box>
                </Paper>
              ) : (
                <Paper elevation={0} sx={{ p: 4, mb: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    Architectural Review Request Form
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ mb: 3 }}>
                    Complete this form to submit your request for architectural modifications or improvements to your property.
                  </Typography>
                  
                  {submitError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {submitError}
                    </Alert>
                  )}
                  
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          label="Full Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          fullWidth
                          required
                          error={!!errors.name}
                          helperText={errors.name}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Property Address"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          fullWidth
                          required
                          error={!!errors.address}
                          helperText={errors.address}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <HomeIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Phone Number"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          fullWidth
                          required
                          error={!!errors.phone}
                          helperText={errors.phone}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Project Description"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          fullWidth
                          required
                          multiline
                          rows={4}
                          error={!!errors.description}
                          helperText={errors.description || "Please provide a detailed description of your proposed modifications"}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 3 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Attach Supporting Documents (Optional)
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Upload diagrams, plans, specifications, or photos
                          </Typography>
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadFileIcon />}
                            sx={{ mt: 1 }}
                          >
                            Select Files
                            <input
                              type="file"
                              hidden
                              multiple
                              onChange={handleFileChange}
                            />
                          </Button>
                          
                          {files.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                Selected files:
                              </Typography>
                              <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                                {files.map((file, index) => (
                                  <ListItem key={index}>
                                    <ListItemIcon>
                                      <DescriptionIcon />
                                    </ListItemIcon>
                                    <ListItemText 
                                      primary={file.name} 
                                      secondary={`${(file.size / 1024).toFixed(1)} KB`} 
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={isSubmitting}
                            sx={{ minWidth: 120 }}
                          >
                            {isSubmitting ? <CircularProgress size={24} /> : "Submit Request"}
                          </Button>
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={handleReset}
                            disabled={isSubmitting}
                          >
                            Reset Form
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                </Paper>
              )}
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Process Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <InfoIcon color="primary" sx={{ mr: 1, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2">
                      What happens next?
                    </Typography>
                    <Typography variant="body2" paragraph>
                      After submission, your request will be reviewed by the Architectural Review Committee (ARC).
                    </Typography>
                    <Typography variant="body2">
                      The review process typically takes up to 30 days from the date of submission.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
              
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Guidelines
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="body2" paragraph>
                  The following projects typically require ARC approval:
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Exterior painting with color changes" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Additions or structural modifications" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Fences, retaining walls, or major landscaping" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Patios, decks, or outdoor structures" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Solar panels or major equipment installation" />
                  </ListItem>
                </List>
                
                <Button 
                  variant="text" 
                  href="/covenants" 
                  sx={{ mt: 2 }}
                  fullWidth
                >
                  View Complete Guidelines
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default ARCRequestForm; 